import { ElementNode, $applyNodeReplacement, $getSelection, $isRangeSelection, $isTextNode } from 'lexical';
import { $findMatchingParent } from '@lexical/utils';
import { sectionHeader } from '../Themes/EditorClasses';

// Emit a class (not Quill's legacy data-header) to avoid a double chevron; importDOM still accepts data-header for migration.

export class SectionHeaderNode extends ElementNode {
    static getType() {
        return 'section-header';
    }

    static clone(node) {
        return new SectionHeaderNode(node.__key);
    }

    createDOM() {
        const element = document.createElement('mark');
        element.className = sectionHeader;
        return element;
    }

    updateDOM() {
        return false;
    }

    static importDOM() {
        return {
            mark: (domNode) => {
                if (domNode.hasAttribute('data-header') || domNode.classList.contains(sectionHeader)) {
                    return { conversion: () => ({ node: $createSectionHeaderNode() }), priority: 1 };
                }
                return null;
            },
        };
    }

    // Public HTML output never shows the marker text: mirrors the legacy backend transform
    // (RichTextUtility.ApplyGhostAnchorTransformations turns <mark data-header> into an empty
    // <span data-anchor>) so ProjectSectionStory.vue picks it up as a table-of-contents entry
    // instead of visible copy. $getChildNodes: () => [] stops the real text from being exported too.
    exportDOM() {
        const element = document.createElement('span');
        element.setAttribute('data-anchor', this.getTextContent());
        return { element, $getChildNodes: () => [] };
    }

    static importJSON() {
        return $createSectionHeaderNode();
    }

    exportJSON() {
        return {
            ...super.exportJSON(),
            type: 'section-header',
            version: 1,
        };
    }

    isInline() {
        return true;
    }

    // Required whenever isInline() is true, or Lexical won't auto-remove the node once its text is deleted,
    // leaving an empty, un-deletable <mark> that traps the caret.
    canBeEmpty() {
        return false;
    }

    // Match LinkNode: typing at the boundaries must not extend the mark.
    canInsertTextBefore() {
        return false;
    }

    canInsertTextAfter() {
        return false;
    }
}

export function $createSectionHeaderNode() {
    return $applyNodeReplacement(new SectionHeaderNode());
}

export function $isSectionHeaderNode(node) {
    return node instanceof SectionHeaderNode;
}

export function $toggleSectionHeader() {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;

    for (const node of selection.getNodes()) {
        const existing = $findMatchingParent(node, $isSectionHeaderNode);
        if (existing) {
            existing.getChildren().forEach(child => existing.insertBefore(child));
            existing.remove();
            return;
        }
    }

    if (selection.isCollapsed()) return;

    // extract() splits partially-selected text nodes so we wrap exactly the selection.
    const textNodes = selection.extract().filter(node => $isTextNode(node) && node.isAttached());
    if (!textNodes.length) return;

    // MVP: only wrap a run within a single parent (a section header sits on one line).
    const parent = textNodes[0].getParent();
    if (!parent || !textNodes.every(node => node.getParent() === parent)) return;

    const wrapper = $createSectionHeaderNode();
    textNodes[0].insertBefore(wrapper);
    textNodes.forEach(node => wrapper.append(node));
}
